import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { api } from "../lib/api";
import { SITE_URL } from "../lib/site";
import {
  DEFAULT_LOCALE,
  LOCALES,
  immediateLocale,
  localeCookie,
  localeForCountry,
} from "./locale";

/*
 * English is bundled; every other language is a chunk fetched when it is
 * first needed.
 *
 * Both languages used to ship in the main bundle, so every visitor downloaded
 * ~150 KB of Indonesian they might never read — and each language added
 * would have added its own. English is the fallback and the language most
 * sessions render in, so it stays inline and the first paint needs nothing
 * else. Another language is loaded through an i18next backend that resolves
 * to a dynamic import, which Rollup emits as one chunk per language (see
 * `manualChunks` in vite.config.ts), so a switch costs one request.
 *
 * Still found rather than listed: a language or a namespace is added by
 * adding its files and naming the language in `languages.json`.
 */
const bundled = import.meta.glob<Record<string, unknown>>(`../locales/en/*.json`, {
  eager: true,
  import: "default",
});
const lazy = import.meta.glob<Record<string, unknown>>(
  ["../locales/*/*.json", "!../locales/en/*.json"],
  { import: "default" },
);

const namespaceOf = (path: string) => /\/([^/]+)\.json$/.exec(path)?.[1];

const english: Record<string, Record<string, unknown>> = {};
for (const [path, messages] of Object.entries(bundled)) {
  const namespace = namespaceOf(path);
  if (namespace) english[namespace] = messages;
}

/**
 * The loader i18next calls for a language that is not bundled. A namespace
 * nobody translated resolves to an empty bundle rather than an error, so the
 * fallback language answers for it instead of the whole switch failing.
 */
const backend = {
  type: "backend" as const,
  init() {},
  read(
    language: string,
    namespace: string,
    callback: (error: unknown, data?: Record<string, unknown>) => void,
  ) {
    const load = lazy[`../locales/${language}/${namespace}.json`];
    if (!load) {
      callback(null, {});
      return;
    }
    load().then(
      (messages) => callback(null, messages),
      (error: unknown) => callback(error),
    );
  },
};

const browser = typeof window !== "undefined";

/*
 * `initAsync: false` initialises synchronously for the bundled language, so a
 * module that imports this and renders immediately — every test does — finds
 * English in place. For another language the backend has to answer first;
 * `ready` is that, and `detectLocale` waits on it before the first render.
 */
const ready = i18n
  .use(backend)
  .use(initReactI18next)
  .init({
    resources: { [DEFAULT_LOCALE]: english },
    partialBundledLanguages: true,
    lng: (browser && immediateLocale(window.location.search, document.cookie)) || DEFAULT_LOCALE,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: [...LOCALES],
    ns: Object.keys(english),
    defaultNS: "common",
    initAsync: false,
    interpolation: { escapeValue: false },
  });

if (browser) {
  document.documentElement.lang = i18n.language;
  i18n.on("languageChanged", (locale) => {
    document.documentElement.lang = locale;
  });
}

const GEO_TIMEOUT_MS = 1500;

/**
 * Settles the language before the first render, for a visitor nothing has told
 * us about yet: no `?lang=` and no remembered choice. The country comes from the
 * API, because this page is static and cannot read its own request. Bounded, so
 * a slow answer costs a moment of English rather than a blank screen.
 */
export async function detectLocale(): Promise<void> {
  await ready;
  if (!browser || immediateLocale(window.location.search, document.cookie)) return;
  try {
    const geo = await Promise.race([
      // Uncredentialed: the route answers `*` since 2026-09-20, which a browser
      // refuses on a request that carries a cookie -- the announcements trap.
      api<{ country: string | null }>("/v1/geo", { anonymous: true, omitCredentials: true }),
      new Promise<never>((_, reject) => setTimeout(() => reject(new Error("timeout")), GEO_TIMEOUT_MS)),
    ]);
    const locale = localeForCountry(geo.country);
    if (locale) await i18n.changeLanguage(locale);
  } catch {
    /* English, which is what the page already is. */
  }
}

/**
 * An explicit choice, or `null` to hand the decision back to detection. It is
 * remembered in the cookie the marketing site reads too, so the two agree.
 */
export async function chooseLocale(locale: string | null): Promise<void> {
  if (browser) {
    document.cookie = localeCookie(
      locale,
      window.location.hostname,
      SITE_URL,
      window.location.protocol === "https:",
    );
  }
  if (locale) {
    await i18n.changeLanguage(locale);
  } else {
    await i18n.changeLanguage(DEFAULT_LOCALE);
    await detectLocale();
  }
}

export { i18n };
export { Trans, useTranslation } from "react-i18next";
